import { normalizeCountry, parsePrivacyRegime } from '@krevya/shared';
import { consent, type PrivacyRegion } from './consent.js';

/**
 * Asks the Worker which privacy model applies, once per page load.
 *
 * Until the answer arrives the consent store behaves strictly and the banner
 * stays hidden, so nothing optional runs early and a US visitor never sees a
 * banner flash. Any failure (offline, a timeout, an answer that is not JSON)
 * resolves to the strict model, which shows the banner.
 *
 * The answer is kept in memory only. The country is never written to storage:
 * it is asked again on the next page load, which is one small request.
 */

/** Long enough for a slow mobile connection; short enough that a hung request does not hide the banner for good. */
const TIMEOUT_MS = 5000;

type Fetcher = (input: string, init: RequestInit) => Promise<Response>;

/** The browser's own Global Privacy Control flag. */
export function browserGpc(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (navigator as Navigator & { globalPrivacyControl?: unknown }).globalPrivacyControl === true;
}

/** The region for this page; never rejects. */
export async function fetchPrivacyRegion(
  fetcher: Fetcher = (input, init) => fetch(input, init),
  gpc: boolean = browserGpc(),
  timeoutMs = TIMEOUT_MS,
): Promise<PrivacyRegion> {
  const strict: PrivacyRegion = { regime: 'strict', gpc, country: null };
  const controller = typeof AbortController === 'undefined' ? null : new AbortController();
  const timer = setTimeout(() => controller?.abort(), timeoutMs);
  try {
    const response = await fetcher('/api/geo', {
      // No cookies: the answer depends on the country alone.
      credentials: 'omit',
      cache: 'no-store',
      headers: { accept: 'application/json' },
      ...(controller ? { signal: controller.signal } : {}),
    });
    if (!response.ok) return strict;
    const body = (await response.json()) as {
      privacyRegime?: unknown;
      gpc?: unknown;
      country?: unknown;
    } | null;
    if (!body || typeof body !== 'object') return strict;
    return {
      regime: parsePrivacyRegime(body.privacyRegime),
      gpc: gpc || body.gpc === true,
      country: normalizeCountry(typeof body.country === 'string' ? body.country : null),
    };
  } catch {
    return strict;
  } finally {
    clearTimeout(timer);
  }
}

let started = false;

/** Starts the question for the page's one consent store. Safe to call more than once. */
export function resolvePrivacyRegion(): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  void fetchPrivacyRegion().then((region) => consent.setRegion(region));
}
